// src/review/canvas/sections/ThreatOverviewSection.jsx
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Stack } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import EditableField from '../shared/EditableField';
import { useReview } from '../../context/ReviewContext';
import {
  persistScenarioName,
  persistScenarioImpact,
} from '../../../redux/slices/securitySlice';
import EditableImpactField from '../shared/EditableImpactField';

/**
 * ThreatOverviewSection - Shows basic threat information
 * Displays threat scenario, category, actor information
 */
function ThreatOverviewSection({ threat }) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();

  // Get template for this assessment to extract dropdown options
  const templateData = useSelector((state) => {
    const assessment = state.assessments?.byId?.[assessmentUuid];
    const templateId = assessment?.template_id;
    return templateId ? state.templates?.byId?.[templateId] : null;
  });

  // Extract impact level options from template (for CIA triad)
  const impactLevelOptions = useMemo(() => {
    if (!templateData?.taxonomy?.securityTaxonomy?.security) {
      return ['None', 'Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical']; // Fallback
    }
    const impactLevels = templateData.taxonomy.securityTaxonomy.security.find(
      (cat) => cat.name === 'Impact Levels'
    );
    return impactLevels?.schema?.options || ['None', 'Negligible', 'Low', 'Moderate', 'Significant', 'High', 'Critical'];
  }, [templateData]);

  // Handler for saving scenario name
  const handleSaveScenarioName = useCallback(
    async (newValue, oldValue) => {
      await dispatch(
        persistScenarioName({
          modelId,
          assessmentUuid,
          scenarioId: threat.scenarioId || threat.threatId,
          name: newValue,
        })
      );
    },
    [dispatch, modelId, assessmentUuid, threat.scenarioId, threat.threatId]
  );

  // Handler for saving impact (CIA triad + rationales)
  const handleSaveImpact = useCallback(
    async (impactUpdates) => {
      await dispatch(
        persistScenarioImpact({
          modelId,
          assessmentUuid,
          scenarioId: threat.scenarioId || threat.threatId,
          impactUpdates,
        })
      );
    },
    [dispatch, modelId, assessmentUuid, threat.scenarioId, threat.threatId]
  );

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        {/* Threat Scenario Name */}
        <EditableField
          label="Threat Scenario"
          value={threat.threatScenario}
          itemType="threats"
          itemId={threat.threatId}
          fieldName="threatScenario"
          onSave={handleSaveScenarioName}
          multiline
          minRows={3}
          placeholder="Describe the threat scenario..."
        />

        {/* CIA Triad Impact Assessment */}
        {threat.impact && typeof threat.impact === 'object' && (
          <Stack gap="sm">
            <EditableImpactField
              label="Confidentiality Impact"
              impactKey="confidentiality"
              values={threat.impact.confidentiality || ''}
              rationale={threat.impact.confidentialityRationale || ''}
              itemType="threats"
              itemId={threat.threatId}
              fieldName="impact.confidentiality"
              onSave={handleSaveImpact}
              impactLevelOptions={impactLevelOptions}
            />
            <EditableImpactField
              label="Integrity Impact"
              impactKey="integrity"
              values={threat.impact.integrity || ''}
              rationale={threat.impact.integrityRationale || ''}
              itemType="threats"
              itemId={threat.threatId}
              fieldName="impact.integrity"
              onSave={handleSaveImpact}
              impactLevelOptions={impactLevelOptions}
            />
            <EditableImpactField
              label="Availability Impact"
              impactKey="availability"
              values={threat.impact.availability || ''}
              rationale={threat.impact.availabilityRationale || ''}
              itemType="threats"
              itemId={threat.threatId}
              fieldName="impact.availability"
              onSave={handleSaveImpact}
              impactLevelOptions={impactLevelOptions}
            />
          </Stack>
        )}
        {threat.impact && typeof threat.impact === 'string' && (
          <EditableField
            label="Potential Impact"
            value={threat.impact}
            itemType="threats"
            itemId={threat.threatId}
            fieldName="impact"
            multiline
            minRows={2}
            placeholder="Describe the potential impact if exploited..."
          />
        )}
      </Stack>
    </Card>
  );
}

ThreatOverviewSection.propTypes = {
  threat: PropTypes.shape({
    threatId: PropTypes.string.isRequired,
    threatScenario: PropTypes.string,
    category: PropTypes.string,
    threatActor: PropTypes.string,
    attackVector: PropTypes.string,
    impact: PropTypes.string,
  }).isRequired,
};

export default ThreatOverviewSection;
